// Full-screen viewer for pictures, PDFs, videos and voice notes in texts and team chat.
//
// Before, a picture opened as a new page: in the iPhone home-screen app there's no browser bar there, so there was
// no way back to the conversation. Now it opens over the conversation with a close (×) button, and closes with a
// swipe down, the Escape key, or the phone's/browser's Back.

import { h } from "./ui";
import { icon } from "./icons";

export type ViewItem = { url: string; type: string; name?: string | null; preview?: string };

let open: { el: HTMLElement; items: ViewItem[]; i: number; pushed: boolean; onKey: (e: KeyboardEvent) => void } | null = null;

export const viewerOpen = () => !!open;

/** Called first by the app's Back handler: true = Back closed the viewer (nothing else to do). */
export function viewerHandlesPop(): boolean {
  if (!open) return false;
  open.pushed = false; // that history entry is gone already
  closeViewer();
  return true;
}

/** Closes the viewer (and drops the history entry it added, so Back doesn't need pressing twice). */
export function closeViewer() {
  if (!open) return;
  const o = open;
  open = null;
  document.removeEventListener("keydown", o.onKey, true);
  o.el.classList.add("closing");
  setTimeout(() => o.el.remove(), 160);
  document.documentElement.classList.remove("viewer-on");
  if (o.pushed) {
    afterOwnBack?.();
    history.back();
  }
}

/** The app counts history.back() calls it makes itself (main.ts ignorePops). */
let afterOwnBack: (() => void) | null = null;
export function setViewerBackHook(fn: () => void) {
  afterOwnBack = fn;
}

const label = (x: ViewItem) =>
  x.name ?? (x.type === "application/pdf" ? "PDF document" : x.type.startsWith("video/") ? "Video" : x.type.startsWith("audio/") ? "Voice note" : x.type.startsWith("image/") ? "Picture" : "Attachment");

/** Opens `items[index]` full screen (left/right goes through the others). */
export function openViewer(items: ViewItem[], index = 0) {
  if (open) closeViewer();
  const stage = h("div", { class: "viewer-stage" });
  const title = h("div", { class: "viewer-title" });
  const count = h("div", { class: "viewer-count" });
  const prev = h("button", { class: "viewer-nav prev", type: "button", "aria-label": "Previous", onclick: (e: MouseEvent) => (e.stopPropagation(), show(cur - 1)) }, icon("back"));
  const next = h("button", { class: "viewer-nav next", type: "button", "aria-label": "Next", onclick: (e: MouseEvent) => (e.stopPropagation(), show(cur + 1)) }, icon("chevron"));
  const save = h("button", { class: "viewer-btn", type: "button", "aria-label": "Save or share", onclick: (e: MouseEvent) => (e.stopPropagation(), void saveItem(items[cur])) }, icon("download"));
  const close = h("button", { class: "viewer-btn viewer-close", type: "button", "aria-label": "Close", onclick: (e: MouseEvent) => (e.stopPropagation(), closeViewer()) }, icon("close"));
  const el = h("div", { class: "viewer", role: "dialog", "aria-modal": "true", "aria-label": "Picture viewer" },
    h("div", { class: "viewer-top" }, h("div", { class: "viewer-head" }, title, count), save, close),
    stage, prev, next);
  let cur = index;

  function show(i: number) {
    if (i < 0 || i >= items.length) return;
    cur = i;
    if (open) open.i = i;
    const x = items[i];
    title.textContent = label(x);
    count.textContent = items.length > 1 ? `${i + 1} of ${items.length}` : "";
    prev.hidden = i === 0;
    next.hidden = i === items.length - 1;
    zoomed = false;
    stage.classList.remove("zoomed");
    stage.replaceChildren(
      x.type.startsWith("image/") ? h("img", { class: "viewer-img", src: x.url, alt: label(x), draggable: false })
      : x.type.startsWith("video/") ? h("video", { class: "viewer-media", src: x.url, controls: true, playsInline: true, autoplay: true })
      : x.type.startsWith("audio/") ? h("audio", { class: "viewer-audio", src: x.url, controls: true, autoplay: true })
      : x.type === "application/pdf" ? h("iframe", { class: "viewer-doc", src: x.url, title: label(x) })
      : h("div", { class: "viewer-other" }, icon("download", "lg"), h("p", {}, label(x)), h("button", { class: "btn", type: "button", onclick: () => void saveItem(x) }, "Download")));
  }

  // Tap the dark area to close; double-tap a picture to zoom in (drag to look around), again to zoom out.
  let zoomed = false;
  let lastTap = 0;
  stage.addEventListener("click", (e) => {
    const onImg = (e.target as HTMLElement).classList.contains("viewer-img");
    if (!onImg) {
      if (e.target === stage) closeViewer();
      return;
    }
    const now = Date.now();
    if (now - lastTap < 300) {
      zoomed = !zoomed;
      stage.classList.toggle("zoomed", zoomed);
      if (zoomed) {
        const img = e.target as HTMLImageElement;
        const r = img.getBoundingClientRect();
        requestAnimationFrame(() => {
          stage.scrollLeft = ((e.clientX - r.left) / r.width) * stage.scrollWidth - stage.clientWidth / 2;
          stage.scrollTop = ((e.clientY - r.top) / r.height) * stage.scrollHeight - stage.clientHeight / 2;
        });
      }
      lastTap = 0;
    } else lastTap = now;
  });

  // Swipe down to close; swipe left/right for the next/previous picture (not while zoomed in).
  let sx = 0, sy = 0, dx = 0, dy = 0, tracking = false;
  stage.addEventListener("touchstart", (e) => {
    if (zoomed || e.touches.length !== 1) return (tracking = false);
    tracking = true;
    sx = e.touches[0].clientX;
    sy = e.touches[0].clientY;
    dx = dy = 0;
  }, { passive: true });
  stage.addEventListener("touchmove", (e) => {
    if (!tracking || e.touches.length !== 1) return;
    dx = e.touches[0].clientX - sx;
    dy = e.touches[0].clientY - sy;
    if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
      stage.style.transform = `translateY(${dy}px)`;
      el.style.setProperty("--fade", String(Math.max(0.35, 1 - dy / 500)));
    }
  }, { passive: true });
  stage.addEventListener("touchend", () => {
    if (!tracking) return;
    tracking = false;
    stage.style.transform = "";
    el.style.removeProperty("--fade");
    if (dy > 110 && dy > Math.abs(dx)) closeViewer();
    else if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) show(cur + (dx < 0 ? 1 : -1));
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") (e.preventDefault(), e.stopPropagation(), closeViewer());
    else if (e.key === "ArrowLeft") (e.preventDefault(), show(cur - 1));
    else if (e.key === "ArrowRight") (e.preventDefault(), show(cur + 1));
  };
  document.addEventListener("keydown", onKey, true);

  // A history entry for the viewer: Back (Android back button, browser back, a back swipe) closes it.
  let pushed = false;
  try {
    history.pushState({ ...(history.state ?? {}), viewer: 1 }, "", location.href);
    pushed = true;
  } catch {
    // too many entries: the × still closes it
  }
  open = { el, items, i: index, pushed, onKey };
  document.documentElement.classList.add("viewer-on");
  document.body.append(el);
  show(index);
  close.focus({ preventScroll: true });
}

/** Save / share: on a phone this opens the share sheet (Save Image, AirDrop, …); elsewhere it downloads. */
async function saveItem(x: ViewItem) {
  const name = x.name || `joy-${Date.now()}.${(x.type.split("/")[1] ?? "bin").replace("jpeg", "jpg").replace(/[^a-z0-9]/g, "")}`;
  try {
    const blob = await (await fetch(x.url, { credentials: "same-origin" })).blob();
    const file = new File([blob], name, { type: blob.type || x.type });
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    if (nav.canShare?.({ files: [file] }) && /iPhone|iPad|Android/.test(navigator.userAgent)) {
      await navigator.share({ files: [file] });
      return;
    }
    const a = h("a", { href: URL.createObjectURL(blob), download: name });
    document.body.append(a);
    a.click();
    setTimeout(() => (URL.revokeObjectURL(a.href), a.remove()), 1000);
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return; // closed the share sheet
    const a = h("a", { href: x.url, download: name });
    document.body.append(a);
    a.click();
    a.remove();
  }
}
